import React, { useState, useRef } from 'react';

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
  step?: number;
  /** Accessible name prefix for the two thumbs (e.g. "Cost"). */
  ariaLabel?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  formatLabel = (v) => v.toString(),
  step = 1,
  ariaLabel = 'Range',
}) => {
  const [minVal, maxVal] = value;
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const getPercent = (val: number) => {
    if (max === min) return 0;
    return ((val - min) / (max - min)) * 100;
  };

  const clampMin = (v: number) => Math.max(min, Math.min(v, maxVal - step));
  const clampMax = (v: number) => Math.min(max, Math.max(v, minVal + step));

  // ── Pointer Events (audit #222): works for mouse, touch and pen ──
  const handlePointerDown = (thumb: 'min' | 'max') => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(thumb);
  };

  const handlePointerMove = (thumb: 'min' | 'max') => (e: React.PointerEvent<HTMLDivElement>) => {
    // Only react while this thumb is actively being dragged (pointer captured).
    if (isDragging !== thumb || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    let newValue = min + (percent / 100) * (max - min);
    newValue = Math.round(newValue / step) * step;

    if (thumb === 'min') {
      onChange([clampMin(newValue), maxVal]);
    } else {
      onChange([minVal, clampMax(newValue)]);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsDragging(null);
  };

  // ── Keyboard support (audit #222) ──
  const handleKeyDown = (thumb: 'min' | 'max') => (e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        delta = -step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        delta = step;
        break;
      case 'PageDown':
        delta = -step * 10;
        break;
      case 'PageUp':
        delta = step * 10;
        break;
      case 'Home':
        e.preventDefault();
        if (thumb === 'min') onChange([min, maxVal]);
        else onChange([minVal, clampMax(minVal + step)]);
        return;
      case 'End':
        e.preventDefault();
        if (thumb === 'min') onChange([clampMin(maxVal - step), maxVal]);
        else onChange([minVal, max]);
        return;
      default:
        return;
    }
    e.preventDefault();
    if (thumb === 'min') {
      onChange([clampMin(minVal + delta), maxVal]);
    } else {
      onChange([minVal, clampMax(maxVal + delta)]);
    }
  };

  const minPercent = getPercent(minVal);
  const maxPercent = getPercent(maxVal);

  const thumbBaseStyle = (thumb: 'min' | 'max', percent: number): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    left: `${percent}%`,
    transform: 'translate(-50%, -50%)',
    width: '20px',
    height: '20px',
    background: 'white',
    border: '2px solid #F9550D',
    borderRadius: '50%',
    cursor: 'grab',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: isDragging === thumb ? 'none' : 'left 0.1s ease',
    zIndex: isDragging === thumb ? 3 : 2,
    touchAction: 'none',
    outlineOffset: '2px',
  });

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Value labels */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '12px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#374151'
      }}>
        <span>{formatLabel(minVal)}</span>
        <span>{formatLabel(maxVal)}</span>
      </div>

      {/* Slider track */}
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          height: '6px',
          background: '#E5E7EB',
          borderRadius: '3px',
          cursor: 'pointer'
        }}
      >
        {/* Active range */}
        <div style={{
          position: 'absolute',
          height: '100%',
          background: '#F9550D',
          borderRadius: '3px',
          left: `${minPercent}%`,
          width: `${maxPercent - minPercent}%`
        }} />

        {/* Min thumb */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={`${ariaLabel} minimum`}
          aria-valuemin={min}
          aria-valuemax={maxVal - step}
          aria-valuenow={minVal}
          aria-valuetext={formatLabel(minVal)}
          aria-orientation="horizontal"
          onPointerDown={handlePointerDown('min')}
          onPointerMove={handlePointerMove('min')}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown('min')}
          style={thumbBaseStyle('min', minPercent)}
        />

        {/* Max thumb */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={`${ariaLabel} maximum`}
          aria-valuemin={minVal + step}
          aria-valuemax={max}
          aria-valuenow={maxVal}
          aria-valuetext={formatLabel(maxVal)}
          aria-orientation="horizontal"
          onPointerDown={handlePointerDown('max')}
          onPointerMove={handlePointerMove('max')}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown('max')}
          style={thumbBaseStyle('max', maxPercent)}
        />
      </div>
    </div>
  );
};

export default RangeSlider;
